import React, { useState } from 'react';
import {
  CameraIcon,
  QrCodeIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

const ITEMS = [
  {
    id: 'mods',
    label: 'Mods and media',
    detail: 'Parts lists, photos, and history together',
    image: '/images/brand/workshop-mod-detail.jpg',
    imageAlt: 'Hands working on a modification in a workshop',
    Icon: CameraIcon,
    chip: '12 mods · 3 albums',
    samples: ['Intake · AEM', 'Coilovers · BC', 'Exhaust · Custom'],
  },
  {
    id: 'events',
    label: 'Events and awards',
    detail: 'Recognition stays on the build',
    image: '/images/brand/paddock-meet-dusk.jpg',
    imageAlt: 'Cars gathered at a dusk paddock meet',
    Icon: TrophyIcon,
    chip: 'Best Engine',
    samples: ['Registered from garage', 'Scores on the profile', 'Award attached'],
  },
  {
    id: 'share',
    label: 'Share anywhere',
    detail: 'Public link and QR for the paddock',
    image: '/images/brand/home-qr-windshield.jpg',
    imageAlt: 'QR code on a car windshield for sharing the build',
    Icon: QrCodeIcon,
    chip: 'QR ready',
    samples: ['Public build link', 'Printable sticker', 'Scan at the show'],
  },
] as const;

/**
 * Interactive proof band under the hero.
 * Film-strip of three image frames (distinct from HowBiltWorks split layout).
 */
export const HomeProofStrip: React.FC = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      className="relative border-y border-white/[0.06] bg-[#0c0c0c] overflow-hidden"
      aria-label="What Bilt keeps with the car"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(249,115,22,0.05),transparent_50%)]"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <ul
          className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 list-none"
          onMouseLeave={() => setActive(0)}
        >
          {ITEMS.map((item, index) => {
            const selected = index === active;
            const Icon = item.Icon;
            return (
              <li key={item.id} className="min-h-0">
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  onMouseEnter={() => setActive(index)}
                  onFocus={() => setActive(index)}
                  className={`group relative w-full min-h-[17.5rem] sm:min-h-[18.5rem] text-left rounded-2xl overflow-hidden border transition-all duration-500 ease-out active:scale-[0.99] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${
                    selected
                      ? 'border-orange-500/40 shadow-[0_0_0_1px_rgba(249,115,22,0.12)]'
                      : 'border-white/[0.08] opacity-[0.88] hover:opacity-100'
                  }`}
                  aria-current={selected ? 'true' : undefined}
                >
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out ${
                      selected ? 'scale-105' : 'scale-100 group-hover:scale-[1.03]'
                    }`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                  <div
                    className={`absolute inset-0 transition-colors duration-500 ${
                      selected
                        ? 'bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/55 to-[#0a0a0a]/20'
                        : 'bg-gradient-to-t from-[#0a0a0a]/95 via-[#0a0a0a]/70 to-[#0a0a0a]/45'
                    }`}
                    aria-hidden
                  />

                  <div className="relative h-full flex flex-col justify-end p-4 sm:p-5">
                    <span
                      className={`mb-auto inline-flex h-9 w-9 items-center justify-center rounded-xl border backdrop-blur-sm transition-colors ${
                        selected
                          ? 'border-orange-500/40 bg-orange-500/20 text-orange-300'
                          : 'border-white/15 bg-[#0a0a0a]/50 text-gray-300'
                      }`}
                      aria-hidden
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                    </span>

                    <p className="font-display text-lg sm:text-xl font-semibold tracking-tight text-white mt-6">
                      {item.label}
                    </p>
                    <p className="text-gray-300 text-sm mt-1.5 leading-snug max-w-[26ch]">
                      {item.detail}
                    </p>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-out ${
                        selected ? 'max-h-28 opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
                      }`}
                    >
                      <p className="font-mono text-[10px] uppercase tracking-wider text-orange-400/90 mb-2">
                        {item.chip}
                      </p>
                      <ul className="space-y-1">
                        {item.samples.map((sample) => (
                          <li
                            key={sample}
                            className="text-xs text-gray-300 flex items-center gap-2"
                          >
                            <span
                              className="h-1 w-1 rounded-full bg-orange-400 shrink-0"
                              aria-hidden
                            />
                            {sample}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default HomeProofStrip;
